import { useEffect, useState } from 'react';
import { Modal, Button, useToast } from '@textile-erp/ui';
import { createApiClient, buildBackendUrl } from '@textile-erp/core';
import { Eye, EyeOff, KeyRound, Loader2, Mail, RefreshCw } from 'lucide-react';

function generateSuggestedPassword(email?: string): string {
  const digits = '23456789';
  const special = '@#$!';
  const rand = (s: string) => s[Math.floor(Math.random() * s.length)];

  const localPart = email ? email.split('@')[0].replace(/[^a-zA-Z]/g, '') : '';
  if (localPart.length >= 3) {
    const base = localPart.slice(0, 5);
    const word = base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
    return word + rand(special) + rand(digits) + rand(digits);
  }

  // Fallback: fully random
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const parts = [
    rand(upper), rand(upper),
    rand(lower), rand(lower), rand(lower),
    rand(digits), rand(digits),
    rand(special),
  ];
  for (let i = parts.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [parts[i], parts[j]] = [parts[j], parts[i]];
  }
  return parts.join('');
}

const apiClient = createApiClient(buildBackendUrl('/api'));

interface ChangeOwnPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

export function ChangeOwnPasswordModal({ isOpen, onClose, email }: ChangeOwnPasswordModalProps) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedPassword, setSuggestedPassword] = useState('');
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem('textile_erp_token');
      if (token) apiClient.setAuthToken(token);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowOld(false);
      setShowNew(false);
      setShowConfirm(false);
      setError(null);
      setSuggestedPassword(generateSuggestedPassword(email));
    }
  }, [isOpen]);

  const validate = (): string | null => {
    if (!oldPassword) return 'Old password is required';
    if (!newPassword) return 'New password is required';
    if (newPassword.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(newPassword)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(newPassword)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(newPassword)) return 'Password must contain at least one number';
    if (newPassword !== confirmPassword) return 'New password and confirm password do not match';
    if (newPassword === oldPassword) return 'New password must be different from old password';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await apiClient.post('/auth/change-password', { oldPassword, newPassword });
      showSuccess('Password updated', 'Your password has been changed successfully');
      onClose();
    } catch (err: any) {
      const msg = err?.message || 'Failed to change password';
      setError(msg);
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Password" size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="email"
              value={email}
              readOnly
              disabled
              className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-theme-secondary cursor-not-allowed"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">Old Password</label>
          <div className="relative">
            <input
              type={showOld ? 'text' : 'password'}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter your current password"
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
            />
            <button
              type="button"
              onClick={() => setShowOld((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showOld ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">New Password</label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={suggestedPassword || 'Enter new password'}
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-theme-secondary">At least 8 characters, with uppercase, lowercase, and a number.</p>
            {suggestedPassword && (
              <button
                type="button"
                onClick={() => { setNewPassword(suggestedPassword); setConfirmPassword(suggestedPassword); setSuggestedPassword(generateSuggestedPassword(email)); }}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 whitespace-nowrap ml-2"
              >
                <RefreshCw className="h-3 w-3" /> Use suggested
              </button>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-theme-primary mb-1">Confirm New Password</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating…
              </>
            ) : (
              <>
                <KeyRound className="h-4 w-4 mr-2" /> Update Password
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
